import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type {
  ApiClient, Elektroverteiler, Elektroverteilerliste, Elektroverteilerangabe,
  Elektrostromkreis, Elektrostromkreisangabe, Elektrobauteilart,
  Elektroverteilerdoku, Trafobelueftungslauf,
} from '../api';
import { IconWarnung } from '../Icons';
import { ElektroAbgaenge } from './ElektroAbgaenge';

// Wie im Bestand: projekt ist optional, weil der Rahmen es erst nach dem Laden
// setzt. Ein pflichtiges Feld waere gelogen.
interface Rahmen { api: ApiClient; projekt?: { id: string } }

/**
 * Die Verteilerpflege.
 *
 * --- Warum diese Seite die wichtigste des Moduls ist ---------------------------
 *
 * Im Modell ist nichts verschaltet — alle elektrischen Werte stehen auf null. Die
 * Zuordnung der 28.000 Bauteile zu Verteilkreisen entsteht ausschließlich hier,
 * und zwar abgeleitet:
 *
 *   Bauteil → Raum → elt_uv im Raumbuch → Verteilerbereich → Verteiler
 *
 * Der Verteilerbereich ist das Bindeglied. Ein Verteiler ohne Bereich bleibt ohne
 * Bauteile, auch wenn er sonst vollständig gepflegt ist — das ist die häufigste
 * Verwechslung und wird deshalb angezeigt.
 *
 * --- Die offenen Bereiche als Arbeitsliste --------------------------------------
 *
 * Oben stehen die Verteilerbereiche aus dem Raumbuch, für die noch kein Verteiler
 * existiert. Solange einer dort steht, bleiben die Bauteile dieser Räume ohne
 * Zuordnung. Die Liste ist damit nicht Auskunft, sondern Aufgabe.
 */
export function ElektroVerteiler() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [liste, setListe] = useState<Elektroverteilerliste | null>(null);
  const [arten, setArten] = useState<Elektrobauteilart[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const [neuAnlegen, setNeuAnlegen] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [bilanz, setBilanz] = useState<Exclude<Awaited<
    ReturnType<ApiClient['elektroBilanzRechnen']>>, void> | null>(null);
  const [bilanzLaeuft, setBilanzLaeuft] = useState(false);

  const laden = async () => {
    if (!projekt) return;

    try {
      const [v, a] = await Promise.all([
        api.elektroVerteiler(projekt.id),
        api.elektroBauteilarten(),
      ]);

      setListe(v);
      setArten(a.filter((x) => x.wirdGefuehrt));
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { laden(); }, [projekt?.id]);

  /*
   * Die Anzeige gliedert nach GESCHOSS, nicht nach Einspeisungshierarchie.
   *
   * Ein Verteiler steht in einem Geschoss — UV_2.0.7 im G00 — und danach sucht man
   * ihn. Die Hierarchie über oberId bleibt in den Daten und erscheint an der Zeile
   * als Einspeisung; als Gliederung war sie unbrauchbar, weil sie bei den
   * Unterverteilungen fast immer leer ist.
   */

  /**
   * Verteiler aus allen Raumbuchbereichen anlegen.
   *
   * Erst prüfen, dann anlegen. Bei 613 Räumen mit über vierzig Bereichen ist ein
   * Lauf, der zur Hälfte danebentrifft, schwerer zu berichtigen als einer, der gar
   * nicht erst lief.
   */
  const ausRaumbuch = async (nurPruefen: boolean) => {
    if (!projekt) return;

    if (!nurPruefen && !confirm(
      'Für jeden Verteilerbereich im Raumbuch einen Verteiler anlegen?\n\n'
      + 'Die Bezeichnungen sind vorläufig und sollten danach fachlich benannt '
      + 'werden. Vorhandene Verteiler bleiben unberührt.'
    )) return;

    setLaeuft(true);
    try {
      const a = await api.elektroVerteilerAusRaumbuch(projekt.id, nurPruefen);

      setMeldung(a ? a.message : 'Angelegt.');
      if (!nurPruefen) await laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  /**
   * Die automatisch angelegten Verteiler entfernen und neu bilden.
   *
   * Nötig, weil frühere Fassungen erfundene Sammelschienen angelegt haben — je
   * erster Zahl eine, dann je Geschoss. Beides sind keine Betriebsmittel, und sie
   * stehen jetzt ohne Bereich und ohne Bauteile in der Liste.
   *
   * Von Hand angelegte Verteiler bleiben unberührt.
   */
  const zuruecksetzen = async (nurPruefen: boolean) => {
    if (!projekt) return;

    if (!nurPruefen && !confirm(
      'Alle automatisch angelegten Verteiler entfernen?\n\n'
      + 'Von Hand angelegte bleiben. Die Bauteile verlieren ihre Zuordnung und '
      + 'bekommen sie über "Zuordnung erneuern" zurück.\n\n'
      + 'Danach: "Alle Bereiche anlegen", dann "Zuordnung erneuern".'
    )) return;

    setLaeuft(true);
    try {
      const a = await api.elektroVerteilerZuruecksetzen(projekt.id, nurPruefen);

      setMeldung(a ? a.message : 'Zurückgesetzt.');
      if (!nurPruefen) await laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  /**
   * Die Leistungsbilanz kaskadieren — UV → NSHV → Trafo → MS-Anlage, mit
   * einem eigenen Gleichzeitigkeitsfaktor je Ebene (Migration 0548).
   *
   * Erst prüfen, dann schreiben: dieselbe Regel wie bei den anderen
   * Ableitungsläufen dieser Seite.
   */
  const bilanzRechnen = async (nurPruefen: boolean) => {
    if (!projekt) return;

    setBilanzLaeuft(true);
    try {
      const a = await api.elektroBilanzRechnen(projekt.id, nurPruefen);
      setBilanz(a ?? null);
      setMeldung(a ? a.message : 'Bilanz gerechnet.');
      if (!nurPruefen) await laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setBilanzLaeuft(false);
    }
  };

  const zuordnungErneuern = async (nurPruefen: boolean) => {
    if (!projekt) return;

    setLaeuft(true);
    try {
      const a = await api.elektroZuordnungErneuern(projekt.id, nurPruefen);

      setMeldung(a ? a.message : 'Zuordnung erneuert.');
      if (!nurPruefen) await laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  if (!projekt) return <div className="leer">wird geladen …</div>;
  if (fehler) return <div className="hinweis fehler">{fehler}</div>;
  if (!liste) return <div className="leer">wird geladen …</div>;

  const gesamtBauteile = liste.verteiler.reduce((n, v) => n + v.bauteile, 0);

  return (
    <>
      <h2 className="karte-titel" style={{ marginTop: 0 }}>
        Verteiler und Stromkreise
        <span className="schwach">
          {liste.verteiler.length} Verteiler · {gesamtBauteile} Bauteil(e)
          zugeordnet
        </span>
      </h2>

      {meldung && (
        <div className="hinweis" style={{ marginBottom: 12 }}>
          {meldung}
          <button onClick={() => setMeldung(null)}
                  style={{ marginLeft: 10, padding: '1px 7px', fontSize: 11 }}>
            schließen
          </button>
        </div>
      )}

      {/* --- Die offenen Bereiche: die Arbeitsliste --------------------------- */}
      {liste.offeneBereiche.length > 0 && (
        <div className="karte" style={{ borderLeft: '3px solid var(--warnung)' }}>
          <h3 style={{ marginTop: 0, fontSize: 14, fontWeight: 500 }}>
            <IconWarnung size={14} /> Verteilerbereiche ohne Verteiler
          </h3>

          <p className="schwach" style={{ fontSize: 12, maxWidth: 720 }}>
            Diese Bereiche stehen im Raumbuch unter <em>Verteilerbereich Strom</em>
            {' '}oder <em>IT</em>, aber es gibt keinen Verteiler dazu. Solange das so
            ist, bleiben die Bauteile dieser Räume ohne Zuordnung.
          </p>

          <p className="schwach" style={{ fontSize: 12 }}>
            <strong>{liste.offeneBereiche.length}</strong> Bereich(e) offen —
            {' '}einzeln über die Knöpfe unten, oder in einem Zug über
            {' '}<em>Alle Bereiche anlegen</em>.
          </p>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {liste.offeneBereiche.map((b) => (
              <button
                key={b.bereich}
                onClick={() => setNeuAnlegen(b.bereich)}
                style={{ padding: '4px 10px', fontSize: 12 }}
                title={`Verteiler für ${b.bereich} anlegen`}
              >
                {b.bereich}
                <span className="sehr-schwach" style={{ marginLeft: 6 }}>
                  {b.raeume} Raum/Räume
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* --- Die Werkzeuge ---------------------------------------------------- */}
      <div className="karte">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
                      alignItems: 'center' }}>
          <button onClick={() => ausRaumbuch(false)} disabled={laeuft}
                  className="betont" style={{ fontSize: 12 }}
                  title="Für jeden Bereich aus dem Raumbuch einen Verteiler">
            Alle Bereiche anlegen
          </button>

          <button onClick={() => zuruecksetzen(false)} disabled={laeuft}
                  style={{ fontSize: 12 }}
                  title="Automatisch angelegte entfernen — von Hand angelegte bleiben">
            zurücksetzen
          </button>

          <button
            onClick={async () => {
              if (!projekt) return;
              setLaeuft(true);
              try {
                const a = await api.elektroVerteilerVerknuepfen(projekt.id, false);
                setMeldung(a
                  ? a.message + '\n' + a.hinweise.slice(0, 8).join('\n')
                  : 'Verknüpft.');
                await laden();
              } catch (e) {
                setMeldung(e instanceof Error ? e.message : String(e));
              } finally {
                setLaeuft(false);
              }
            }}
            disabled={laeuft}
            style={{ fontSize: 12 }}
            title="Über die Bezeichnung des Schranks im Modell — bringt Raum und Koordinaten"
          >
            mit Modell verknüpfen
          </button>

          <button onClick={() => ausRaumbuch(true)} disabled={laeuft}
                  style={{ fontSize: 12 }}
                  title="Rechnen, aber nichts anlegen">
            prüfen
          </button>

          <div style={{ width: 1, height: 22, background: 'var(--linie, #ddd)' }} />

          <button onClick={() => setNeuAnlegen('')} style={{ fontSize: 12 }}>
            einzeln anlegen
          </button>

          <div style={{ width: 1, height: 22, background: 'var(--linie, #ddd)' }} />

          {/* Erst prüfen, dann übernehmen. Die Prüfung ändert nichts — bei 28.000
              Bauteilen ist ein Lauf, der zur Hälfte danebentrifft, schwerer zu
              berichtigen als einer, der gar nicht erst lief. */}
          <button onClick={() => zuordnungErneuern(true)} disabled={laeuft}
                  style={{ fontSize: 12 }}
                  title="Rechnen, aber nichts ändern">
            Zuordnung prüfen
          </button>

          <button onClick={() => zuordnungErneuern(false)} disabled={laeuft}
                  style={{ fontSize: 12 }}>
            Zuordnung erneuern
          </button>

          <span className="sehr-schwach" style={{ fontSize: 11, marginLeft: 4 }}>
            leitet die Verteiler aus dem Raumbuch ab — von Hand gesetzte bleiben
          </span>
        </div>
      </div>

      {/* --- Die Leistungsbilanz (V-E3, Migration 0548) ---------------------- */}
      <div className="karte">
        <h3 style={{ marginTop: 0, fontSize: 14, fontWeight: 500 }}>
          Leistungsbilanz
        </h3>

        <p className="schwach" style={{ fontSize: 12, maxWidth: 720 }}>
          Kaskadiert von der Unterverteilung über die NSHV bis zum
          Transformator und zur MS-Anlage — jede Einspeiseebene mit einem
          eigenen Gleichzeitigkeitsfaktor (üblicher Wert, unbestätigt,
          Fachplanerfrage 276).
        </p>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={() => bilanzRechnen(true)} disabled={bilanzLaeuft}
                  style={{ fontSize: 12 }} title="Rechnen, aber nichts schreiben">
            Bilanz prüfen
          </button>

          <button onClick={() => bilanzRechnen(false)} disabled={bilanzLaeuft}
                  className="betont" style={{ fontSize: 12 }}>
            Bilanz rechnen
          </button>

          <div style={{ width: 1, height: 22, background: 'var(--linie, #ddd)' }} />

          <button
            onClick={async () => {
              if (!projekt) return;
              setBilanzLaeuft(true);
              try {
                await api.elektroBilanzberichtLaden(projekt.id);
              } catch (e) {
                setMeldung(e instanceof Error ? e.message : String(e));
              } finally {
                setBilanzLaeuft(false);
              }
            }}
            disabled={bilanzLaeuft}
            style={{ fontSize: 12 }}
            title="Kennwerte je Ebene, Trafo-Auslastung, alle Verteiler und die Annahmen"
          >
            Bilanzbericht (ELT-BIL)
          </button>

          <button
            onClick={async () => {
              if (!projekt) return;
              setBilanzLaeuft(true);
              try {
                await api.elektroUebersichtsschaltplanLaden(projekt.id);
              } catch (e) {
                setMeldung(e instanceof Error ? e.message : String(e));
              } finally {
                setBilanzLaeuft(false);
              }
            }}
            disabled={bilanzLaeuft}
            style={{ fontSize: 12 }}
            title="MS-Anlage bis Unterverteilung als ein Blatt"
          >
            Übersichtsschaltplan
          </button>
        </div>

        {bilanz && (
          <div className="tabellenrahmen" style={{ marginTop: 10 }}>
            <table style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>Ebene</th>
                  <th>Anlagen</th>
                  <th>Bedarf in kW</th>
                  <th>Anschluss in kVA</th>
                </tr>
              </thead>
              <tbody>
                {bilanz.stand.map((s) => (
                  <tr key={s.art}>
                    <td>{s.art}</td>
                    <td>{s.anlagen}</td>
                    <td>{s.bedarfKW.toLocaleString('de-DE')}</td>
                    <td>{s.anschlussKVA.toLocaleString('de-DE')}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {bilanz.trafoAuslastung.length > 0 && (
              <table style={{ fontSize: 12, marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>Transformator</th>
                    <th>Bemessungsleistung in kVA</th>
                    <th>Bedarf in kW</th>
                    <th>Auslastung in %</th>
                  </tr>
                </thead>
                <tbody>
                  {bilanz.trafoAuslastung.map((t) => (
                    <tr key={t.kennzeichen}>
                      <td>{t.kennzeichen}</td>
                      <td>{t.bemessungsleistungKva.toLocaleString('de-DE')}</td>
                      <td>{t.bedarfKW.toLocaleString('de-DE')}</td>
                      <td style={t.auslastungProzent && t.auslastungProzent > 100
                        ? { color: 'var(--zustand-fehler)', fontWeight: 600 } : undefined}>
                        {t.auslastungProzent?.toLocaleString('de-DE') ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* --- Der Arbeitsvorrat ---------------------------------------

                Bis hierher zeigte die Bilanz nur Summen. Eine Kette, die
                reißt, sieht in einer Summe aus wie eine Kette mit dem
                Ergebnis null — deshalb steht der Vorrat jetzt daneben, je
                Gruppe gezählt und mit Kennzeichen.

                Warnfarbe und Ausrufezeichen, nicht Rot: Das ist ein offener
                Arbeitspunkt, kein Fehler. Rot bleibt dem tatsächlichen
                Problem vorbehalten (überlasteter Trafo). */}
            {bilanz.luecken.length > 0 && (
              <div style={{ marginTop: 10 }}>
                {bilanz.luecken.map((l) => (
                  <div key={l.schluessel}
                       style={{ marginTop: 8, fontSize: 12,
                                borderLeft: '3px solid var(--warnung, #ef6c00)',
                                paddingLeft: 8 }}>
                    <div style={{ fontWeight: 600 }}>
                      {l.titel}: {l.anzahl}
                    </div>

                    <div className="schwach" style={{ maxWidth: 780 }}>
                      {l.erklaerung}
                    </div>

                    <div className="sehr-schwach"
                         style={{ fontSize: 11, marginTop: 2 }}>
                      {l.kennzeichen.join(', ')}
                      {l.anzahl > l.kennzeichen.length
                        && ` … und ${l.anzahl - l.kennzeichen.length} weitere`}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* --- Die Sicherheitsstromversorgung, GETRENNT ----------------

                Eine eigene Tabelle und nicht eine Zeile in der Kaskade: Die
                34 SV-Verteiler hängen absichtlich an keiner NSHV, sondern an
                einer der drei Zentralbatterieanlagen. Ihre Last in dieselben
                Spalten zu mischen hieße, die Trennung aufzuheben, die der
                ganze Zweck der Sicherheitsversorgung ist.

                „—" in der Auslastung heißt „nicht zu bilden" (kein
                Typenschild-VA gepflegt) und ausdrücklich nicht „null". */}
            {bilanz.svBilanzen.length > 0 && (
              <>
                <div className="schwach" style={{ fontSize: 12, marginTop: 12 }}>
                  Sicherheitsstromversorgung — eigene Stufe, getrennt von der
                  Normalstrombilanz
                </div>

                <table style={{ fontSize: 12, marginTop: 4 }}>
                  <thead>
                    <tr>
                      <th>Anlage</th>
                      <th>SV-Verteiler</th>
                      <th>Leuchten</th>
                      <th>davon aus Annahme</th>
                      <th>Bedarf in W</th>
                      <th>Bemessung in VA</th>
                      <th>Auslastung in %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bilanz.svBilanzen.map((z) => (
                      <tr key={z.zentraleId}>
                        <td title={z.kette}>
                          {z.kennzeichen}
                          {z.bezeichnung && (
                            <span className="sehr-schwach"
                                  style={{ marginLeft: 4 }}>
                              {z.bezeichnung}
                            </span>
                          )}
                        </td>
                        <td>{z.verteiler}</td>
                        <td>{z.leuchten.toLocaleString('de-DE')}</td>
                        <td title={'Ohne gepflegte Leistung — für sie gilt die '
                                 + 'unbestätigte Vorgabe sv_leistung_je_leuchte_w.'}>
                          {z.leuchtenAusAnnahme.toLocaleString('de-DE')}
                        </td>
                        <td>{z.bedarfW.toLocaleString('de-DE',
                          { maximumFractionDigits: 0 })}</td>
                        <td>{z.bemessungsleistungVa?.toLocaleString('de-DE') ?? '—'}</td>
                        <td style={z.auslastungProzent && z.auslastungProzent > 100
                          ? { color: 'var(--zustand-fehler)', fontWeight: 600 }
                          : undefined}>
                          {z.auslastungProzent?.toLocaleString('de-DE') ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {bilanz.hinweise.length > 0 && (
              <ul className="schwach"
                  style={{ fontSize: 11, marginTop: 10, paddingLeft: 18 }}>
                {bilanz.hinweise.map((h, i) => <li key={i}>{h}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* --- Reste aus früheren Fassungen ----------------------------------- */}
      {(() => {
        // Verteiler ohne Bereich, die die Plattform angelegt hat: erfundene
        // Sammelschienen aus früheren Fassungen. Sie tragen keine Bauteile und
        // gehören entfernt.
        const reste = liste.verteiler.filter(
          (v) => v.herkunft === 'import' && !v.verteilerbereich);

        if (reste.length === 0) return null;

        return (
          <div className="karte" style={{ borderLeft: '3px solid var(--warnung)' }}>
            <h3 style={{ marginTop: 0, fontSize: 14, fontWeight: 500 }}>
              <IconWarnung size={14} /> {reste.length} Verteiler ohne Bereich
            </h3>

            <p className="schwach" style={{ fontSize: 12, maxWidth: 720 }}>
              Sie stammen aus früheren Fassungen des Anlegelaufs, der
              Sammelschienen je Bereich beziehungsweise je Geschoss erzeugte —
              {' '}<em>{reste.slice(0, 4).map((v) => v.kennzeichen).join(', ')}
              {reste.length > 4 ? ' …' : ''}</em>.
              {' '}Ein „Quadrant 2" oder „Geschoss G00" ist kein Betriebsmittel; die
              Gliederung entsteht in der Baumansicht. Bitte zurücksetzen und neu
              anlegen.
            </p>

            <button onClick={() => zuruecksetzen(false)} disabled={laeuft}
                    className="betont" style={{ fontSize: 12 }}>
              zurücksetzen und neu bilden
            </button>
          </div>
        );
      })()}

      {/* --- Die Hierarchie -------------------------------------------------- */}
      {liste.verteiler.length === 0 ? (
        <div className="karte">
          <div className="leer">
            Noch kein Verteiler angelegt.
            {liste.offeneBereiche.length > 0
              ? ' Oben stehen die Bereiche aus dem Raumbuch — ein Klick legt einen'
                + ' Verteiler dafür an.'
              : ' Und im Raumbuch ist kein Verteilerbereich gepflegt — ohne ihn'
                + ' kann nichts zugeordnet werden.'}
          </div>
        </div>
      ) : (
        <div className="karte">
          {/* --- Etagenweise ------------------------------------------------
              Ein Verteiler steht in einem Geschoss — UV_2.0.7 im G00. Danach
              sucht man ihn, nicht in einer Einspeisungshierarchie.

              Die echte Hierarchie über OberId bleibt bestehen und erscheint an
              der Zeile als Einspeisung. */}
          {Object.entries(
            liste.verteiler.reduce((acc, v) => {
              const g = v.geschoss ?? 'ohne Geschoss';
              (acc[g] ??= []).push(v);
              return acc;
            }, {} as Record<string, Elektroverteiler[]>),
          )
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([geschoss, imGeschoss]) => (
              <div key={geschoss} style={{ marginBottom: 14 }}>
                <div className="feldname"
                     style={{ marginBottom: 4, display: 'flex', gap: 8 }}>
                  <span>{geschoss}</span>
                  <span className="sehr-schwach" style={{ fontWeight: 400 }}>
                    {imGeschoss.length} Verteiler
                    {' · '}
                    {imGeschoss.reduce((n, v) => n + v.bauteile, 0)
                      .toLocaleString('de-DE')} Bauteil(e)
                  </span>
                </div>

                {imGeschoss
                  .sort((a, b) =>
                    (a.quadrant ?? '').localeCompare(b.quadrant ?? '')
                    || (a.verteilernummer ?? '').localeCompare(
                         b.verteilernummer ?? '')
                    || a.kennzeichen.localeCompare(b.kennzeichen))
                  .map((v) => (
                    <Verteilerzweig
                      key={v.id}
                      v={v}
                      kinder={() => []}
                      tiefe={0}
                      api={api}
                      projektId={projekt.id}
                      arten={arten}
                      alle={liste.verteiler}
                      offen={offen}
                      umschalten={(id) => setOffen(offen === id ? null : id)}
                      melde={(m) => { setMeldung(m); laden(); }}
                    />
                  ))}
              </div>
            ))}

          {/* Freihängende: ihr Oberer wurde entfernt. Sichtbar, damit sie nicht
              unbemerkt aus dem Baum fallen. */}
          {liste.verteiler
            .filter((v) => v.oberId && !liste.verteiler.some(
              (x) => x.id === v.oberId))
            .map((v) => (
              <div key={v.id} className="hinweis" style={{ marginTop: 8 }}>
                <IconWarnung size={13} />
                <div style={{ fontSize: 12 }}>
                  <strong>{v.kennzeichen}</strong> hängt an einem Verteiler, den es
                  nicht mehr gibt. Bitte neu einordnen.
                </div>
              </div>
            ))}
        </div>
      )}

      {neuAnlegen !== null && (
        <Verteilerformular
          api={api}
          projektId={projekt.id}
          bereich={neuAnlegen}
          alle={liste.verteiler}
          schliessen={() => setNeuAnlegen(null)}
          fertig={(m) => { setNeuAnlegen(null); setMeldung(m); laden(); }}
        />
      )}
    </>
  );
}

// === Ein Zweig ===============================================================

function Verteilerzweig({
  v, kinder, tiefe, api, projektId, arten, alle, offen, umschalten, melde,
}: {
  v: Elektroverteiler;
  kinder: (id: string) => Elektroverteiler[];
  tiefe: number;
  api: ApiClient;
  projektId: string;
  arten: Elektrobauteilart[];
  alle: Elektroverteiler[];
  offen: string | null;
  umschalten: (id: string) => void;
  melde: (m: string) => void;
}) {
  const eigene = kinder(v.id);
  const istOffen = offen === v.id;

  const artname: Record<string, string> = {
    einspeisung: 'Einspeisung',
    trafo: 'Transformator',
    nshv: 'Niederspannungshauptverteilung',
    uv: 'Unterverteilung',
    sv: 'Sicherheitsverteilung',
  };

  return (
    <>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 8px', paddingLeft: 8 + tiefe * 22,
          borderBottom: '1px solid var(--linie, #eee)',
          cursor: 'pointer',
          background: istOffen ? 'var(--flaeche, #fafafa)' : undefined,
        }}
        onClick={() => umschalten(v.id)}
      >
        <span className="sehr-schwach" style={{ fontSize: 10, width: 10 }}>
          {istOffen ? '−' : '+'}
        </span>

        <span style={{ fontWeight: 500, fontSize: 13 }}>{v.kennzeichen}</span>

        <span className="marke neutral" style={{ fontSize: 10 }}>
          {artname[v.art] ?? v.art}
        </span>

        {/* Herkunft 'virtuell': in der Plattform angelegt, weil die Planung
            den Knoten braucht (Festlegung Fachplaner 19.09.2026,
            'einspeisung-verketten --virtuelle-nshv') — im Modell NICHT
            vorhanden und dort nachzuführen. Die Bilanz führt seine Zahlen
            wie jeden anderen Knoten; ablesbar muss bleiben, dass er eine
            Planungsannahme ist. */}
        {v.herkunft === 'virtuell' && (
          <span className="marke warnung" style={{ fontSize: 10 }}
                title={v.hinweis
                  ?? 'Virtuell angelegt — im Modell nicht vorhanden, dort '
                    + 'nachzuführen.'}>
            virtuell — im Modell nachführen
          </span>
        )}

        {/* Was der Name bedeutet, ausgeschrieben.
            UV_2.0.7 heisst Quadrant 2, Geschoss G00, Unterverteilung 7 — das
            steht sonst nur im Namen, und wer die Ordnung nicht kennt, liest ihn
            als drei beliebige Zahlen. */}
        {v.quadrant && (
          <span className="sehr-schwach" style={{ fontSize: 10 }}>
            Q{v.quadrant}
            {v.geschoss ? ` · ${v.geschoss}` : ''}
            {v.verteilernummer ? ` · UV ${v.verteilernummer}` : ''}
          </span>
        )}

        {v.bezeichnung && (
          <span className="schwach" style={{ fontSize: 12 }}>{v.bezeichnung}</span>
        )}

        <span style={{ flex: 1 }} />

        {/* Der Verteilerbereich ist das Bindeglied zum Raumbuch. Fehlt er, bleibt
            der Verteiler ohne Bauteile — und das ist die häufigste Verwechslung. */}
        {/* --- Drei Sachverhalte, nicht einer ------------------------------ */}
        {/*
          Vorher stand bei jedem ohne Bereich eine Warnmarke "kein Bereich". Bei
          einem Trafo ist das falsch: Er versorgt keinen Raumbereich, er speist
          Verteiler — seine Zuordnung steht in der Trafogruppe.

          Und bei einer Showpower-Verteilung mit 650 kW auch nicht: Dort sind die
          Bühnenanschlüsse nicht im Modell, und die Leistung kommt aus der
          Netzberechnung.

          Nur der dritte Fall ist ein Mangel — eine Unterverteilung ohne Bereich
          bleibt ohne Bauteile.
        */}
        {v.verteilerbereich ? (
          <span className="sehr-schwach" style={{ fontSize: 11 }}>
            {v.verteilerbereich}
            {v.weitereRaeume && (
              <span className="marke neutral"
                    style={{ fontSize: 9, marginLeft: 4 }}
                    title={`Weitere Schrankfelder in ${v.weitereRaeume}`}>
                +{v.weitereRaeume.split(',').length}&nbsp;Raum
              </span>
            )}
          </span>

        ) : v.trafogruppe ? (
          // Ein Trafo oder eine NSHV: Die Gruppe ist die Zuordnung.
          <span className="marke neutral" style={{ fontSize: 10 }}
                title={'Speist über die Trafogruppe, nicht über einen Raumbereich. '
                     + (v.trafogruppe.startsWith('S_')
                        ? 'ACHTUNG Südnetz: Die Kupplung ist im normalen Betrieb '
                        + 'geschlossen — vier Trafos parallel, Kurzschlussstrom '
                        + 'annähernd doppelt.'
                        : 'Im Nordnetz ist die Kupplung geöffnet, die Zuordnung '
                        + 'ist eindeutig.')}>
            {v.trafogruppe}
            {v.bemessungsleistung
              ? ` · ${v.bemessungsleistung.toLocaleString('de-DE')} kVA` : ''}
          </span>

        ) : v.bedarfsleistung ? (
          // Eine Verteilung aus der Netzberechnung: Die Leistung ist die Aussage.
          <span className="marke gut" style={{ fontSize: 10 }}
                title="Aus der Netzberechnung. Die Abgänge sind Bühnenanschlüsse und stehen nicht im Modell — deshalb 0 Bauteile.">
            {(v.bedarfsleistung / 1000).toFixed(2)} kW aus Berechnung
          </span>

        ) : v.art === 'ms' ? (
          <span className="marke neutral" style={{ fontSize: 10 }}
                title="Eine Mittelspannungsschaltanlage speist Transformatoren, nicht Räume.">
            speist Trafos
          </span>

        ) : (
          <span className="marke warnung" style={{ fontSize: 10 }}
                title="Ohne Verteilerbereich bleibt er ohne Bauteile">
            kein Bereich
          </span>
        )}

        {/* --- Die Bilanz: Zahl, Lücke oder gar nichts --------------------

            Drei Sachverhalte, und zwei davon sahen gleich aus — eine leere
            Zelle bedeutete sowohl „null Last" als auch „es gibt hier gar
            keine Bilanz, weil die Kette darüber fehlt".

            Am 18.09.2026 stand deshalb bei allen elf Transformatoren nichts,
            obwohl alle elf eine Bemessungsleistung tragen: Keine der sechs
            NSHV hat einen Trafo, also fand die Trafostufe keine Kinder. Wer
            die Liste las, sah eine Bilanz mit dem Ergebnis null.

            `bilanzKette` trägt seit `bilanz-rechnen` den Grund im Klartext
            und beginnt im Lückenfall mit `LUECKE: `. */}
        {v.bilanzKette?.startsWith('LUECKE: ') ? (
          <span className="marke warnung" style={{ fontSize: 10, minWidth: 96,
                                                   textAlign: 'center' }}
                title={v.bilanzKette.slice('LUECKE: '.length)}>
            ! Bilanzlücke
          </span>

        ) : v.bedarfsleistung != null ? (
          <span className="schwach" style={{ fontSize: 11, minWidth: 96,
                                             textAlign: 'right' }}
                title={v.bilanzKette ?? 'Bedarfsleistung aus der Bilanz'}>
            {(v.bedarfsleistung / 1000).toLocaleString('de-DE',
              { maximumFractionDigits: 1 })} kW
          </span>

        ) : (
          <span className="sehr-schwach" style={{ fontSize: 10, minWidth: 96,
                                                  textAlign: 'right' }}
                title={'Für diesen Knoten ist keine Bilanz gerechnet. Das ist '
                     + 'nicht dasselbe wie null Last — „Bilanz prüfen" nennt '
                     + 'den Grund.'}>
            ohne Bilanz
          </span>
        )}

        <span className="schwach" style={{ fontSize: 11, minWidth: 80,
                                           textAlign: 'right' }}>
          {v.bauteile} Bauteil(e)
        </span>

        <span className="schwach" style={{ fontSize: 11, minWidth: 70,
                                           textAlign: 'right' }}>
          {v.stromkreise} Abgang/Abgänge
        </span>
      </div>

      {istOffen && (
        <>
          <Verteilerinhalt
            v={v}
            api={api}
            projektId={projektId}
            arten={arten}
            alle={alle}
            tiefe={tiefe}
            melde={melde}
          />

          {/* --- Die Abgänge ----------------------------------------------
              Unter dem Verteiler, weil sie zu ihm gehören und nicht auf eine
              eigene Seite: Wer einen Abgang bildet, braucht die Bauteilliste
              desselben Verteilers vor sich. */}
          <div style={{ paddingLeft: 20 + tiefe * 16,
                        borderLeft: '2px solid var(--linie, #eee)',
                        marginLeft: 4 }}>
            <ElektroAbgaenge
              api={api}
              verteiler={v}
              geschlossen={() => melde('')}
            />
          </div>
        </>
      )}

      {eigene.map((k) => (
        <Verteilerzweig
          key={k.id}
          v={k}
          kinder={kinder}
          tiefe={tiefe + 1}
          api={api}
          projektId={projektId}
          arten={arten}
          alle={alle}
          offen={offen}
          umschalten={umschalten}
          melde={melde}
        />
      ))}
    </>
  );
}

// === Der Inhalt eines Verteilers =============================================

export function Verteilerinhalt({
  v, api, projektId, arten, alle, tiefe, melde,
}: {
  v: Elektroverteiler;
  api: ApiClient;
  projektId: string;
  arten: Elektrobauteilart[];
  alle: Elektroverteiler[];
  tiefe: number;
  melde: (m: string) => void;
}) {
  const [kreise, setKreise] = useState<Elektrostromkreis[] | null>(null);
  const [neuerKreis, setNeuerKreis] = useState(false);
  const [bearbeiten, setBearbeiten] = useState(false);

  // Stückliste und Wärme werden erst auf Anforderung geholt. Sie werden je
  // Aufruf gerechnet, und 169 Verteiler gleichzeitig zu rechnen, nur weil
  // jemand den Baum aufklappt, wäre teuer und niemandem nützlich.
  const [doku, setDoku] = useState<Elektroverteilerdoku | null>(null);
  const [dokuLaeuft, setDokuLaeuft] = useState(false);

  const dokuLaden = async () => {
    setDokuLaeuft(true);
    try {
      setDoku(await api.elektroStueckliste(v.id));
    } catch (e) {
      melde(e instanceof Error ? e.message : String(e));
    } finally {
      setDokuLaeuft(false);
    }
  };

  // Das Trafo-Datenblatt ELT-TRA — nur an Transformatoren angeboten, siehe
  // die Bedingung unten. Der Bericht bildet ALLE Transformatoren des
  // Projekts auf einmal ab, deshalb reicht projektId als Parameter.
  const [datenblattLaeuft, setDatenblattLaeuft] = useState(false);

  const datenblattLaden = async () => {
    setDatenblattLaeuft(true);
    try {
      await api.elektroTrafodatenblattLaden(projektId);
    } catch (e) {
      melde(e instanceof Error ? e.message : String(e));
    } finally {
      setDatenblattLaeuft(false);
    }
  };

  const laden = async () => {
    try {
      setKreise(await api.elektroStromkreise(v.id));
    } catch {
      setKreise([]);
    }
  };

  useEffect(() => { laden(); }, [v.id]);

  const entfernen = async () => {
    if (!confirm(
      `Verteiler '${v.kennzeichen}' entfernen?\n\n`
      + `${v.bauteile} Bauteil(e) verlieren ihre Zuordnung, `
      + `${v.stromkreise} Abgang/Abgänge werden mitgelöscht.`
    )) return;

    try {
      const a = await api.elektroVerteilerEntfernen(v.id);
      melde(a ? a.message : 'Entfernt.');
    } catch (e) {
      melde(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div style={{
      paddingLeft: 30 + tiefe * 22, paddingRight: 12,
      paddingTop: 10, paddingBottom: 12,
      background: 'var(--flaeche, #fafafa)',
      borderBottom: '1px solid var(--linie, #eee)',
    }}>
      {/* --- Die technischen Daten ------------------------------------------ */}
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12,
                    marginBottom: 10 }}>
        <Angabe name="Raum" wert={v.raumnummer} />
        <Angabe name="Geschoss" wert={v.geschoss} />
        <Angabe name="Bemessungsstrom"
                wert={v.bemessungsstrom ? `${v.bemessungsstrom} A` : null} />
        <Angabe name="Spannung"
                wert={v.bemessungsspannung ? `${v.bemessungsspannung} V` : null} />
        <Angabe name="Kurzschlussstrom"
                wert={v.kurzschlussstrom ? `${v.kurzschlussstrom} kA` : null} />
        <Angabe name="Netzform" wert={v.netzform} />
        <Angabe name="Schutzart" wert={v.schutzart} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button onClick={() => setBearbeiten(true)}
                style={{ padding: '2px 8px', fontSize: 11 }}>
          bearbeiten
        </button>
        <button onClick={() => setNeuerKreis(true)}
                style={{ padding: '2px 8px', fontSize: 11 }}>
          Abgang anlegen
        </button>
        <button onClick={entfernen}
                style={{ padding: '2px 8px', fontSize: 11 }}>
          entfernen
        </button>
        <button onClick={dokuLaden} disabled={dokuLaeuft}
                style={{ padding: '2px 8px', fontSize: 11 }}>
          {dokuLaeuft ? 'rechnet …' : 'Stückliste und Wärme'}
        </button>
      </div>

      {doku && <Verteilerdokumentation doku={doku} />}

      {/* --- Die Belüftung des Aufstellraums ---------------------------------

          Ein EIGENER Abschnitt an der Trafo-Ansicht, ausdrücklicher Auftrag
          des Fachplaners vom 19.09.2026: "die berechnung dann als eigenen
          abschnitt im trafo darstellen".

          Nur bei art === 'trafo': An einer Unterverteilung gibt es keine
          Trafobelüftung, und ein leerer Abschnitt an 169 Verteilern wäre
          Lärm. */}
      {v.art === 'trafo' && (
        <>
          <div style={{ display: 'flex', marginTop: 10 }}>
            <button onClick={datenblattLaden} disabled={datenblattLaeuft}
                    className="betont"
                    style={{ padding: '2px 8px', fontSize: 11 }}>
              {datenblattLaeuft ? 'wird erzeugt …' : 'Datenblatt (ELT-TRA)'}
            </button>
          </div>

          <Trafobelueftungsabschnitt
            api={api}
            projektId={projektId}
            raumnummer={v.raumnummer}
            melde={melde}
          />
        </>
      )}

      {/* --- Die Abgänge ----------------------------------------------------- */}
      {kreise === null ? (
        <div className="leer" style={{ fontSize: 12 }}>Abgänge werden geladen …</div>
      ) : kreise.length === 0 ? (
        <div className="sehr-schwach" style={{ fontSize: 12 }}>
          Noch kein Abgang. Die Bauteile hängen am Verteiler, aber ohne Abgang —
          für eine Prüfung nach DIN VDE 0100 reicht das nicht.
        </div>
      ) : (
        <div className="tabellenrahmen">
          <table className="zuordnung" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Abgang</th>
                <th style={{ textAlign: 'left' }}>Verwendung</th>
                <th style={{ textAlign: 'left' }}>Schutzorgan</th>
                <th style={{ textAlign: 'left' }}>Leitung</th>
                <th style={{ textAlign: 'right' }}>Bauteile</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {kreise.map((k) => (
                <Kreiszeile
                  key={k.id}
                  k={k}
                  v={v}
                  api={api}
                  arten={arten}
                  melde={(m) => { melde(m); laden(); }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {neuerKreis && (
        <Kreisformular
          api={api}
          verteilerId={v.id}
          verteiler={v.kennzeichen}
          schliessen={() => setNeuerKreis(false)}
          fertig={(m) => { setNeuerKreis(false); melde(m); laden(); }}
        />
      )}

      {bearbeiten && (
        <Verteilerformular
          api={api}
          projektId={projektId}
          vorhanden={v}
          alle={alle}
          schliessen={() => setBearbeiten(false)}
          fertig={(m) => { setBearbeiten(false); melde(m); }}
        />
      )}
    </div>
  );
}

function Angabe({ name, wert }: { name: string; wert: string | null }) {
  if (!wert) return null;

  return (
    <div>
      <div className="feldname" style={{ fontSize: 10 }}>{name}</div>
      <div>{wert}</div>
    </div>
  );
}

// === Eine Abgangszeile =======================================================

function Kreiszeile({
  k, v, api, arten, melde,
}: {
  k: Elektrostromkreis;
  v: Elektroverteiler;
  api: ApiClient;
  arten: Elektrobauteilart[];
  melde: (m: string) => void;
}) {
  const [zuweisen, setZuweisen] = useState(false);
  const [art, setArt] = useState('');
  const [laeuft, setLaeuft] = useState(false);

  const entfernen = async () => {
    if (!confirm(`Abgang '${k.kennzeichen}' entfernen?`)) return;

    try {
      const a = await api.elektroStromkreisEntfernen(k.id);
      melde(a ? a.message : 'Entfernt.');
    } catch (e) {
      melde(e instanceof Error ? e.message : String(e));
    }
  };

  const zuordnen = async () => {
    if (!art) return;

    setLaeuft(true);
    try {
      const a = await api.elektroKreisZuweisen(k.id, { bauteilart: art });

      melde(a ? a.message : 'Zugewiesen.');
      setZuweisen(false);
      setArt('');
    } catch (e) {
      melde(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <>
      <tr>
        <td className="zuordnung-name" style={{ fontSize: 12 }}>
          {k.kennzeichen}
          {k.istSicherheit && (
            <span className="marke warnung" style={{ marginLeft: 6, fontSize: 9 }}
                  title="Sicherheitsstromversorgung — keine Allgemeinverbraucher">
              SV
            </span>
          )}
          {k.bezeichnung && (
            <div className="sehr-schwach" style={{ fontSize: 10 }}>
              {k.bezeichnung}
            </div>
          )}
        </td>

        <td className="schwach">{k.verwendung ?? '—'}</td>

        <td className="schwach">
          {k.schutzorgan ?? '—'}
          {k.bemessungsstrom && (
            <span className="sehr-schwach" style={{ marginLeft: 4 }}>
              {k.bemessungsstrom} A
            </span>
          )}
        </td>

        <td className="schwach" style={{ fontSize: 11 }}>
          {k.querschnitt ? `${k.querschnitt} mm²` : ''}
          {k.leitungsart ? ` ${k.leitungsart}` : ''}
          {k.laenge ? ` · ${k.laenge} m` : ''}
          {!k.querschnitt && !k.leitungsart && '—'}
        </td>

        <td style={{ textAlign: 'right' }} className="schwach">{k.bauteile}</td>

        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <button onClick={() => setZuweisen(!zuweisen)}
                  style={{ padding: '1px 6px', fontSize: 10 }}>
            zuweisen
          </button>
          <button onClick={entfernen}
                  style={{ padding: '1px 6px', fontSize: 10, marginLeft: 4 }}>
            löschen
          </button>
        </td>
      </tr>

      {zuweisen && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--flaeche, #f5f5f5)',
                                   padding: '8px 10px' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                          flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12 }}>Bauteilart:</span>

              <select value={art} onChange={(e) => setArt(e.target.value)}
                      style={{ fontSize: 12 }}>
                <option value="">wählen …</option>
                {arten
                  .filter((a) => a.brauchtStromkreis)
                  .map((a) => (
                    <option key={a.schluessel} value={a.schluessel}>
                      {a.bezeichnung}
                    </option>
                  ))}
              </select>

              <button onClick={zuordnen} disabled={!art || laeuft}
                      className="betont" style={{ fontSize: 11 }}>
                zuweisen
              </button>

              <span className="sehr-schwach" style={{ fontSize: 11 }}>
                betrifft alle Bauteile dieser Art, die an {v.kennzeichen} hängen
              </span>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// === Das Verteilerformular ===================================================

function Verteilerformular({
  api, projektId, vorhanden, bereich, alle, schliessen, fertig,
}: {
  api: ApiClient;
  projektId: string;
  vorhanden?: Elektroverteiler;
  bereich?: string;
  alle: Elektroverteiler[];
  schliessen: () => void;
  fertig: (m: string) => void;
}) {
  const [f, setF] = useState<Elektroverteilerangabe>({
    kennzeichen: vorhanden?.kennzeichen ?? bereich ?? '',
    bezeichnung: vorhanden?.bezeichnung ?? '',
    art: vorhanden?.art ?? 'uv',
    oberId: vorhanden?.oberId ?? null,
    verteilerbereich: vorhanden?.verteilerbereich ?? bereich ?? '',
    raumnummer: vorhanden?.raumnummer ?? '',
    geschoss: vorhanden?.geschoss ?? '',
    bemessungsstrom: vorhanden?.bemessungsstrom ?? null,
    bemessungsspannung: vorhanden?.bemessungsspannung ?? 400,
    kurzschlussstrom: vorhanden?.kurzschlussstrom ?? null,
    schutzart: vorhanden?.schutzart ?? '',
    netzform: vorhanden?.netzform ?? 'TN-S',
  });

  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const setzen = (k: keyof Elektroverteilerangabe, w: unknown) =>
    setF({ ...f, [k]: w });

  const speichern = async () => {
    if (!f.kennzeichen?.trim()) {
      setFehler('Ohne Kennzeichen lässt sich ein Verteiler nicht unterscheiden.');
      return;
    }

    setLaeuft(true);
    try {
      const a = vorhanden
        ? await api.elektroVerteilerAendern(vorhanden.id, f)
        : await api.elektroVerteilerAnlegen(projektId, f);

      fertig(a ? a.message : 'Gespeichert.');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 100,
    }} onClick={schliessen}>
      <div className="karte" style={{ maxWidth: 620, width: '90%',
                                      maxHeight: '86vh', overflow: 'auto',
                                      margin: 0 }}
           onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>
          {vorhanden ? `Verteiler ${vorhanden.kennzeichen}` : 'Verteiler anlegen'}
        </h3>

        {fehler && <div className="hinweis fehler">{fehler}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr',
                      gap: 8, alignItems: 'center', fontSize: 13 }}>
          <label>Kennzeichen *</label>
          <input value={f.kennzeichen ?? ''}
                 onChange={(e) => setzen('kennzeichen', e.target.value)}
                 placeholder="UV-E1-01" />

          <label>Bezeichnung</label>
          <input value={f.bezeichnung ?? ''}
                 onChange={(e) => setzen('bezeichnung', e.target.value)}
                 placeholder="Unterverteilung Technik 1. OG Nord" />

          <label>Art</label>
          <select value={f.art ?? 'uv'}
                  onChange={(e) => setzen('art', e.target.value)}>
            <option value="einspeisung">Einspeisung</option>
            <option value="trafo">Transformator</option>
            <option value="nshv">Niederspannungshauptverteilung</option>
            <option value="uv">Unterverteilung</option>
            <option value="sv">Sicherheitsverteilung</option>
          </select>

          <label>hängt an</label>
          <select value={f.oberId ?? ''}
                  onChange={(e) => setzen('oberId', e.target.value || null)}>
            <option value="">— nichts (Wurzel) —</option>
            {alle
              .filter((v) => v.id !== vorhanden?.id)
              .map((v) => (
                <option key={v.id} value={v.id}>
                  {v.kennzeichen} {v.bezeichnung ? `· ${v.bezeichnung}` : ''}
                </option>
              ))}
          </select>

          <label>Verteilerbereich</label>
          <div>
            <input value={f.verteilerbereich ?? ''}
                   onChange={(e) => setzen('verteilerbereich', e.target.value)}
                   style={{ width: '100%' }} />
            <div className="sehr-schwach" style={{ fontSize: 11, marginTop: 2 }}>
              Muss genau so im Raumbuch unter <em>Verteilerbereich Strom</em> oder
              {' '}<em>IT</em> stehen. Ohne ihn bleibt der Verteiler ohne Bauteile.
              <br />
              Aufbau <code>UV_2.0.7</code>: Quadrant 2, Geschoss G00,
              Unterverteilung 7.
            </div>
          </div>

          <label>Raum</label>
          <input value={f.raumnummer ?? ''}
                 onChange={(e) => setzen('raumnummer', e.target.value)}
                 placeholder="E1-042" />

          <label>Geschoss</label>
          <input value={f.geschoss ?? ''}
                 onChange={(e) => setzen('geschoss', e.target.value)}
                 placeholder="G01" />

          <label>Bemessungsstrom</label>
          <input type="number" value={f.bemessungsstrom ?? ''}
                 onChange={(e) => setzen('bemessungsstrom',
                   e.target.value ? Number(e.target.value) : null)}
                 placeholder="A" />

          <label>Spannung</label>
          <input type="number" value={f.bemessungsspannung ?? ''}
                 onChange={(e) => setzen('bemessungsspannung',
                   e.target.value ? Number(e.target.value) : null)}
                 placeholder="V" />

          <label>Kurzschlussstrom</label>
          <input type="number" value={f.kurzschlussstrom ?? ''}
                 onChange={(e) => setzen('kurzschlussstrom',
                   e.target.value ? Number(e.target.value) : null)}
                 placeholder="kA" />

          <label>Netzform</label>
          <select value={f.netzform ?? ''}
                  onChange={(e) => setzen('netzform', e.target.value)}>
            <option value="">—</option>
            <option value="TN-S">TN-S</option>
            <option value="TN-C-S">TN-C-S</option>
            <option value="TN-C">TN-C</option>
            <option value="TT">TT</option>
            <option value="IT">IT</option>
          </select>

          <label>Schutzart</label>
          <input value={f.schutzart ?? ''}
                 onChange={(e) => setzen('schutzart', e.target.value)}
                 placeholder="IP54" />
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16,
                      justifyContent: 'flex-end' }}>
          <button onClick={schliessen}>Abbrechen</button>
          <button onClick={speichern} disabled={laeuft} className="betont">
            {laeuft ? 'wird gespeichert …' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

// === Das Abgangsformular =====================================================

function Kreisformular({
  api, verteilerId, verteiler, schliessen, fertig,
}: {
  api: ApiClient;
  verteilerId: string;
  verteiler: string;
  schliessen: () => void;
  fertig: (m: string) => void;
}) {
  const [f, setF] = useState<Elektrostromkreisangabe>({
    kennzeichen: '',
    bezeichnung: '',
    verwendung: 'beleuchtung',
    schutzorgan: 'B16',
    bemessungsstrom: 16,
    polzahl: 1,
    querschnitt: 1.5,
    leitungsart: 'NYM-J',
    laenge: null,
    istSicherheit: false,
    hinweis: '',
  });

  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const setzen = (k: keyof Elektrostromkreisangabe, w: unknown) =>
    setF({ ...f, [k]: w });

  const speichern = async () => {
    if (!f.kennzeichen?.trim()) {
      setFehler('Ohne Kennzeichen — etwa F1 — lässt sich ein Abgang nicht '
              + 'unterscheiden.');
      return;
    }

    setLaeuft(true);
    try {
      const a = await api.elektroStromkreisAnlegen(verteilerId, f);
      fertig(a ? a.message : 'Angelegt.');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 100,
    }} onClick={schliessen}>
      <div className="karte" style={{ maxWidth: 560, width: '90%', margin: 0,
                                      maxHeight: '86vh', overflow: 'auto' }}
           onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Abgang in {verteiler}</h3>

        {fehler && <div className="hinweis fehler">{fehler}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr',
                      gap: 8, alignItems: 'center', fontSize: 13 }}>
          <label>Kennzeichen *</label>
          <input value={f.kennzeichen ?? ''}
                 onChange={(e) => setzen('kennzeichen', e.target.value)}
                 placeholder="F1" />

          <label>Bezeichnung</label>
          <input value={f.bezeichnung ?? ''}
                 onChange={(e) => setzen('bezeichnung', e.target.value)}
                 placeholder="Beleuchtung Nord" />

          <label>Verwendung</label>
          <select value={f.verwendung ?? ''}
                  onChange={(e) => setzen('verwendung', e.target.value)}>
            <option value="beleuchtung">Beleuchtung</option>
            <option value="steckdosen">Steckdosen</option>
            <option value="kraft">Kraft</option>
            <option value="bma">Brandmeldeanlage</option>
            <option value="eak">Elektroakustik</option>
            <option value="it">IT</option>
            <option value="sonstiges">Sonstiges</option>
          </select>

          <label>Schutzorgan</label>
          <input value={f.schutzorgan ?? ''}
                 onChange={(e) => setzen('schutzorgan', e.target.value)}
                 placeholder="B16 oder C16/3 oder RCD 30mA" />

          <label>Bemessungsstrom</label>
          <input type="number" value={f.bemessungsstrom ?? ''}
                 onChange={(e) => setzen('bemessungsstrom',
                   e.target.value ? Number(e.target.value) : null)}
                 placeholder="A" />

          <label>Polzahl</label>
          <select value={f.polzahl ?? 1}
                  onChange={(e) => setzen('polzahl', Number(e.target.value))}>
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={4}>4</option>
          </select>

          <label>Querschnitt</label>
          <input type="number" step="0.5" value={f.querschnitt ?? ''}
                 onChange={(e) => setzen('querschnitt',
                   e.target.value ? Number(e.target.value) : null)}
                 placeholder="mm²" />

          <label>Leitungsart</label>
          <input value={f.leitungsart ?? ''}
                 onChange={(e) => setzen('leitungsart', e.target.value)}
                 placeholder="NYM-J" />

          <label>Länge</label>
          <input type="number" value={f.laenge ?? ''}
                 onChange={(e) => setzen('laenge',
                   e.target.value ? Number(e.target.value) : null)}
                 placeholder="m" />

          <label>Sicherheitskreis</label>
          <div>
            <input type="checkbox" checked={f.istSicherheit ?? false}
                   onChange={(e) => setzen('istSicherheit', e.target.checked)} />
            <span className="sehr-schwach" style={{ fontSize: 11, marginLeft: 6 }}>
              Sicherheitsstromversorgung — darf keine Allgemeinverbraucher tragen
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16,
                      justifyContent: 'flex-end' }}>
          <button onClick={schliessen}>Abbrechen</button>
          <button onClick={speichern} disabled={laeuft} className="betont">
            {laeuft ? 'wird angelegt …' : 'Anlegen'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Stückliste, Feldbedarf und Wärmebilanz eines Verteilers (Paket V-E2).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DIE HERKUNFT DER VERLUSTLEISTUNG IN DER TABELLE STEHT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Gerätekatalog trägt bisher nur ÜBLICHE Werte — Herkunft 'festlegung'.
 * Eine Wärmebilanz, die darauf ruht, ist eine Abschätzung. Ohne die Spalte
 * sähe sie aus wie eine Rechnung, und genau so würde sie gelesen.
 *
 * Dieselbe Farbregel wie im Feldkatalog: was belegt ist, steht normal; was
 * angesetzt ist, steht gedämpft.
 */
// === Die Trafobelüftung nach AGI J11 =========================================

/**
 * Der Belüftungsnachweis des Aufstellraums — alle Eingangsgrößen, alle
 * Zwischengrößen, das Ergebnis, und je Wert seine Herkunft.
 *
 * ── Warum die Herkunft an JEDEM Wert steht ─────────────────────────────
 *
 * Auf diesem Abschnitt stehen drei Arten von Zahlen nebeneinander: was im
 * Quellblatt GEDRUCKT steht (H, Δϑ_L, ϑ_A), was aus seinen Ergebnissen
 * ZURÜCKGERECHNET ist (die Ausflusszahl), und was DIESE Anwendung rechnet.
 * Ohne die Herkunft sähen alle drei gleich belastbar aus.
 *
 * ── Und warum beide Flächen ────────────────────────────────────────────
 *
 * Seit der Festlegung des Fachplaners vom 19.09.2026 gilt die Zuluftdichte
 * des Belüftungsblatts — das ergibt 1,42 m² statt der 1,37 m², die in der
 * Planungsunterlage stehen. Eine stillschweigende Abweichung von einer
 * Planungsunterlage wäre ein Fehler; eine bezifferte ist eine Entscheidung.
 * Deshalb stehen beide Zahlen nebeneinander und nicht eine an Stelle der
 * anderen.
 *
 * Auf Anforderung geladen und nicht beim Aufklappen des Baums: Der Lauf
 * rechnet alle Aufstellräume des Projekts, und das je Trafo beim Öffnen zu
 * tun wäre elfmal dieselbe Rechnung.
 */
function Trafobelueftungsabschnitt({
  api, projektId, raumnummer, melde,
}: {
  api: ApiClient;
  projektId: string;
  raumnummer: string | null;
  melde: (m: string) => void;
}) {
  const [lauf, setLauf] = useState<Trafobelueftungslauf | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = async () => {
    setLaeuft(true);
    try {
      setLauf(await api.elektroTrafobelueftung(projektId));
    } catch (e) {
      melde(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const n = lauf?.nachweise.find((x) => x.raumnummer === raumnummer) ?? null;

  return (
    <div style={{ marginTop: 6, marginBottom: 14,
                  borderTop: '1px solid var(--linie)', paddingTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                    marginBottom: 8 }}>
        <strong style={{ fontSize: 12 }}>
          Belüftung des Aufstellraums nach AGI J11
        </strong>

        {!lauf && (
          <button onClick={laden} disabled={laeuft}
                  style={{ padding: '2px 8px', fontSize: 11 }}>
            {laeuft ? 'rechnet …' : 'Nachweis rechnen'}
          </button>
        )}
      </div>

      {!lauf ? (
        <div className="sehr-schwach" style={{ fontSize: 11 }}>
          Wird auf Anforderung gerechnet — ein gespeichertes Ergebnis würde
          veralten, sobald jemand einen der sieben Ansätze ändert.
        </div>
      ) : !raumnummer ? (
        <div className="marke warnung" style={{ fontSize: 11 }}>
          Dieser Transformator trägt keine Raumnummer — ohne Aufstellraum gibt
          es keinen Raumnachweis.
        </div>
      ) : !n ? (
        <div className="marke warnung" style={{ fontSize: 11 }}>
          Für {raumnummer} liegt kein Nachweis vor. Eine Belüftung ohne
          bekannte Verlustleistung wäre keine Rechnung, sondern eine Zahl.
        </div>
      ) : (
        <>
          {n.trafos.length > 1 && (
            <div className="sehr-schwach"
                 style={{ fontSize: 11, marginBottom: 6 }}>
              Belüftet wird der RAUM: In {n.raumnummer} stehen
              {' '}{n.trafos.length} Transformatoren ({n.trafos.join(', ')}),
              und die abzuführende Wärme ist ihre Summe.
            </div>
          )}

          <div className="tabellenrahmen" style={{ marginBottom: 8 }}>
            <table className="zuordnung" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Größe</th>
                  <th style={{ textAlign: 'right' }}>Wert</th>
                  <th style={{ textAlign: 'left' }}>Herkunft</th>
                </tr>
              </thead>
              <tbody>
                <Belueftungszeile
                  name="thermisch wirksame Höhe H"
                  wert={`${n.ansaetze.thermischHoeheM.toLocaleString('de-DE')} m`}
                  herkunft="Belüftungsblatt, gedruckt — UNBESTÄTIGT" />
                <Belueftungszeile
                  name="zulässige Raumlufterwärmung Δϑ_L"
                  wert={`${n.ansaetze.lufterwaermungK.toLocaleString('de-DE')} K`}
                  herkunft="Belüftungsblatt, gedruckt — BEFUND: dessen Überschrift nennt 20 K" />
                <Belueftungszeile
                  name="Auslegungs-Außentemperatur ϑ_A = ϑ_zu"
                  wert={`${n.ansaetze.aussentemperaturC.toLocaleString('de-DE')} °C`}
                  herkunft="Belüftungsblatt, gedruckt — UNBESTÄTIGT" />
                <Belueftungszeile
                  name="Ausflusszahl μ"
                  wert={n.ansaetze.ausflusszahl.toLocaleString('de-DE')}
                  herkunft="aus v = 0,51 m/s des Blatts zurückgerechnet — UNBESTÄTIGT" />
                <Belueftungszeile
                  name="spezifische Wärmekapazität c_p"
                  wert={`${n.ansaetze.spezWaermekapazitaetJKgK
                    .toLocaleString('de-DE')} J/(kg·K)`}
                  herkunft="üblicher Wert — UNBESTÄTIGT" />
                <Belueftungszeile
                  name="Abluftzuschlag"
                  wert={`× ${n.ansaetze.abluftZuschlag.toLocaleString('de-DE')}`}
                  herkunft="Belüftungsblatt: „x1,1, Reserve“" />

                <Belueftungszeile
                  name="Verlustleistung P_VT = Σ (P_0 + P_k)"
                  wert={`${n.verlustleistungKw.toLocaleString('de-DE',
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kW`}
                  herkunft="berechnet — Festlegung Fachplaner 19.09.2026: P_V = 12,62 kW" />
                <Belueftungszeile
                  name="angesetzte Luftdichte ρ"
                  wert={`${n.luftdichteAngesetztKgM3.toLocaleString('de-DE',
                    { maximumFractionDigits: 4 })} kg/m³`}
                  herkunft="ZULUFTDICHTE des Belüftungsblatts bei ϑ_A — Festlegung 19.09.2026" />
                <Belueftungszeile
                  name="Abluftdichte ρ(ϑ_A + Δϑ_L)"
                  wert={`${n.abluftdichteKgM3.toLocaleString('de-DE',
                    { maximumFractionDigits: 4 })} kg/m³`}
                  herkunft="berechnet: ρ = 353 / (273,15 + ϑ)" />
                <Belueftungszeile
                  name="Auftriebsdruck Δp = g · H · (ρ_zu − ρ_ab)"
                  wert={`${n.differenzdruckPa.toLocaleString('de-DE',
                    { maximumFractionDigits: 3 })} Pa`}
                  herkunft="berechnet" />
                <Belueftungszeile
                  name="Geschwindigkeit v = μ · √(2Δp/ρ_zu)"
                  wert={`${n.geschwindigkeitMs.toLocaleString('de-DE',
                    { maximumFractionDigits: 3 })} m/s`}
                  herkunft="berechnet" />
                <Belueftungszeile
                  name="Luftmenge V̇ = P_VT / (ρ · c_p · Δϑ_L)"
                  wert={`${Math.round(n.luftmengeM3H).toLocaleString('de-DE')} m³/h`}
                  herkunft="berechnet" />
                <Belueftungszeile
                  name="Zuluftquerschnitt A_ZL = V̇ / (3600 · v)"
                  wert={`${n.zuluftquerschnittM2.toLocaleString('de-DE',
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²`}
                  herkunft="berechnet — MASSGEBEND"
                  hervor />
                <Belueftungszeile
                  name="Abluftquerschnitt A_AL = A_ZL × Zuschlag"
                  wert={`${n.abluftquerschnittM2.toLocaleString('de-DE',
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²`}
                  herkunft="berechnet — MASSGEBEND"
                  hervor />
              </tbody>
            </table>
          </div>

          {/* --- Die Abweichung von der Planungsunterlage, beziffert ------ */}
          <div style={{
            border: '1px solid var(--zustand-warnung-rand)',
            background: 'var(--zustand-warnung-flaeche)',
            borderRadius: 4, padding: '8px 10px', fontSize: 11,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4,
                          color: 'var(--zustand-warnung)' }}>
              Abweichung von der Planungsunterlage
            </div>

            <table className="zuordnung" style={{ width: '100%', fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }} />
                  <th style={{ textAlign: 'right' }}>Luftdichte</th>
                  <th style={{ textAlign: 'right' }}>Luftmenge</th>
                  <th style={{ textAlign: 'right' }}>A_ZL</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Belüftungsblatt (Quelle, 29.06.2026)</td>
                  <td style={{ textAlign: 'right' }}>
                    {n.ansaetze.quellblattDichteKgM3.toLocaleString('de-DE',
                      { maximumFractionDigits: 3 })} kg/m³
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {Math.round(n.luftmengeQuellblattM3H)
                      .toLocaleString('de-DE')} m³/h
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {n.querschnittQuellblattM2.toLocaleString('de-DE',
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²
                  </td>
                </tr>
                <tr>
                  <td>dieser Nachweis (Festlegung 19.09.2026)</td>
                  <td style={{ textAlign: 'right' }}>
                    {n.luftdichteAngesetztKgM3.toLocaleString('de-DE',
                      { maximumFractionDigits: 4 })} kg/m³
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {Math.round(n.luftmengeM3H).toLocaleString('de-DE')} m³/h
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    {n.zuluftquerschnittM2.toLocaleString('de-DE',
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²
                  </td>
                </tr>
                <tr>
                  <td>Unterschied</td>
                  <td style={{ textAlign: 'right' }}>—</td>
                  <td style={{ textAlign: 'right' }}>
                    +{Math.round(n.luftmengeM3H - n.luftmengeQuellblattM3H)
                      .toLocaleString('de-DE')} m³/h
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {n.querschnittQuellblattM2 > 0
                      ? `+${((n.zuluftquerschnittM2 / n.querschnittQuellblattM2 - 1)
                          * 100).toLocaleString('de-DE',
                          { maximumFractionDigits: 1 })} %`
                      : '—'}
                  </td>
                </tr>
              </tbody>
            </table>

            <div style={{ marginTop: 6 }}>
              Das Belüftungsblatt rechnet an zwei Stellen mit zwei Dichten: in
              der Geschwindigkeit mit der Zuluft bei ϑ_A, in der Luftmenge mit
              1,19 kg/m³. Die Festlegung des Fachplaners vom 19.09.2026
              („luftdichte im belüftungsblatt verwenden“) räumt das auf. Der
              Querschnitt wird dadurch GRÖSSER — die Abweichung ist zur
              sicheren Seite.
            </div>
          </div>

          <details className="erlaeuterung" style={{ marginTop: 8 }}>
            <summary style={{ fontSize: 11 }}>Rechenweg im Klartext</summary>
            <div className="sehr-schwach"
                 style={{ fontSize: 11, lineHeight: 1.5 }}>
              {n.kette}
            </div>
          </details>
        </>
      )}
    </div>
  );
}

function Belueftungszeile({
  name, wert, herkunft, hervor,
}: {
  name: string;
  wert: string;
  herkunft: string;
  hervor?: boolean;
}) {
  return (
    <tr>
      <td style={{ fontWeight: hervor ? 600 : undefined }}>{name}</td>
      <td style={{ textAlign: 'right', fontWeight: hervor ? 600 : undefined }}>
        {wert}
      </td>
      <td className="sehr-schwach" style={{ fontSize: 11 }}>{herkunft}</td>
    </tr>
  );
}

function Verteilerdokumentation({ doku }: { doku: Elektroverteilerdoku }) {
  const w = doku.waerme;

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12,
                    marginBottom: 8 }}>
        <Angabe name="belegt" wert={`${doku.teBelegt.toFixed(1)} TE`} />
        <Angabe name="mit Reserve" wert={`${doku.teMitReserve.toFixed(1)} TE`} />
        <Angabe name="TE je Feld"
                wert={doku.teJeFeld ? `${doku.teJeFeld}` : null} />
        <Angabe name="Felder erforderlich"
                wert={doku.felderBedarf ? `${doku.felderBedarf}` : null} />
        <Angabe name="Verlustleistung"
                wert={w ? `${w.wirksameVerlustleistungW.toFixed(0)} W` : null} />
        <Angabe name="Übertemperatur"
                wert={w
                  ? `${w.uebertemperaturK.toFixed(1)} K von `
                    + `${w.zulaessigeUebertemperaturK.toFixed(1)} K`
                  : null} />
        <Angabe name="Lüftungsbedarf"
                wert={w && w.lueftungsbedarfW > 0
                  ? `${w.lueftungsbedarfW.toFixed(0)} W · `
                    + `${w.luftstromM3H.toFixed(0)} m³/h`
                  : w ? 'keiner' : null} />
      </div>

      <div className="tabellenrahmen" style={{ marginBottom: 8 }}>
        <table className="zuordnung" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Pos</th>
              <th style={{ textAlign: 'left' }}>Betriebsmittel</th>
              <th style={{ textAlign: 'right' }}>Anzahl</th>
              <th style={{ textAlign: 'right' }}>TE</th>
              <th style={{ textAlign: 'right' }}>Pv in W</th>
              <th style={{ textAlign: 'left' }}>Verlust aus</th>
              <th style={{ textAlign: 'left' }}>Regel</th>
            </tr>
          </thead>
          <tbody>
            {doku.stueckliste.map((p) => (
              <tr key={p.nummer}>
                <td>{p.nummer}</td>
                <td>{p.bezeichnung}</td>
                <td style={{ textAlign: 'right' }}>{p.anzahl}</td>
                <td style={{ textAlign: 'right' }}>
                  {p.teGesamt === null ? '—' : p.teGesamt.toFixed(1)}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {p.verlustGesamtW === null ? '—' : p.verlustGesamtW.toFixed(1)}
                </td>
                <td className={p.verlustHerkunft === 'hersteller'
                  ? undefined : 'sehr-schwach'}>
                  {p.verlustHerkunft ?? '—'}
                </td>
                <td className="sehr-schwach" style={{ fontSize: 11 }}>
                  {p.regel}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {w && (
        <div className="sehr-schwach" style={{ fontSize: 11, marginBottom: 6 }}>
          {w.befund} — {w.verfahren} ({w.normbezug})
        </div>
      )}

      {doku.befunde.length > 0 && (
        <ul style={{ fontSize: 11, margin: 0, paddingLeft: 18 }}>
          {doku.befunde.map((b, i) => (
            <li key={i} className="sehr-schwach">{b}</li>
          ))}
        </ul>
      )}

      <div className="sehr-schwach" style={{ fontSize: 11, marginTop: 6 }}>
        {doku.klemmen.length} Klemme(n) im Klemmenplan. Die vollständige Liste
        steht im Bericht ELT-VDK.
      </div>
    </div>
  );
}
